/**
 * Iframe Communication
 * Handles embedded iframe detection and parent communication
 */

import { isShopifyEmbed } from './embedConfig'

export interface IframeMessage {
  type: string
  [key: string]: any
}

export interface IframeHeightMessage extends IframeMessage {
  type: 'iframeHeight'
  height: number
}

export interface CustomerStatusMessage extends IframeMessage {
  type: 'customerStatus'
  isloggedIn: boolean
}

export interface ModalStatusMessage extends IframeMessage {
  type: 'modalStatus'
  modalOpened: boolean
}

/**
 * Check if the application is embedded in an iframe
 */
export function isEmbedded(): boolean {
  try {
    return window.self !== window.top
  } catch {
    return true
  }
}

/**
 * Post message to parent frame
 */
export function postMessageToParent(message: IframeMessage): void {
  if (!isEmbedded()) {
    return
  }

  try {
    window.parent.postMessage(message, '*')
  } catch {
    // Cross-origin parents can reject postMessage; the caller cannot act on it.
  }
}

// Shopify only in practice: the sole caller, useCenterIframeOnOpen, is gated by isShopifyEmbed().
export function requestHostViewportCenter(timeoutMs = 150): Promise<number | null> {
  if (!isShopifyEmbed()) {
    return Promise.resolve(null)
  }

  return new Promise(resolve => {
    let settled = false
    const finish = (value: number | null) => {
      if (settled) {
        return
      }
      settled = true
      window.removeEventListener('message', handleMessage)
      resolve(value)
    }
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'hostViewportCenter' && typeof event.data.center === 'number') {
        finish(event.data.center)
      }
    }
    window.addEventListener('message', handleMessage)
    postMessageToParent({ type: 'requestHostViewport' })
    setTimeout(() => finish(null), timeoutMs)
  })
}

/**
 * Setup modal detection for iframe
 */
export function setupModalDetection(): () => void {
  if (!isEmbedded()) {
    return () => {}
  }

  let modalCheckInterval: number
  let modalContentSent = false

  const checkForModal = () => {
    const modalContent = document.querySelector('.modal-content')
    if (modalContent && !modalContentSent) {
      modalContentSent = true
      postMessageToParent({
        type: 'modalStatus',
        modalOpened: true,
      })
      clearInterval(modalCheckInterval)
    }
  }

  modalCheckInterval = window.setInterval(checkForModal, 100)

  // Auto cleanup after 5 seconds
  setTimeout(() => {
    clearInterval(modalCheckInterval)
  }, 5000)

  return () => {
    clearInterval(modalCheckInterval)
  }
}

/**
 * Setup iframe message listeners
 */
export function setupIframeMessageListeners(
  onTranslate?: (language: string) => void,
  onLogout?: () => void,
  onMainMenu?: () => void
): () => void {
  if (!isEmbedded()) {
    return () => {}
  }

  const handleMessage = (event: MessageEvent) => {
    if (!event.data) {
      return
    }

    switch (event.data.type) {
      case 'translate':
        if (onTranslate && event.data.language) {
          onTranslate(event.data.language)
        }
        break

      default:
        // Handle legacy string messages
        if (event.data === 'logout' && onLogout) {
          onLogout()
        } else if (event.data === 'main-menu' && onMainMenu) {
          onMainMenu()
        }
        break
    }
  }

  window.addEventListener('message', handleMessage)

  return () => {
    window.removeEventListener('message', handleMessage)
  }
}

/**
 * Post customer status to parent
 */
export function postCustomerStatus(isLoggedIn: boolean): void {
  if (!isEmbedded()) {
    return
  }

  postMessageToParent({
    type: 'customerStatus',
    isloggedIn: isLoggedIn,
  })
}

/**
 * Add mobile device detection
 */
export function addMobileDeviceDetection(): () => void {
  const handleTouchStart = () => {
    document.body.classList.add('mb-device')
  }

  document.addEventListener('touchstart', handleTouchStart)

  return () => {
    document.removeEventListener('touchstart', handleTouchStart)
  }
}
